import React, { useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Download, Share, Plus, CheckCircle2, Zap, Bell, WifiOff, MousePointerClick, Printer } from "lucide-react";
import { usePWAInstall } from "@/lib/pwa";
import { PushToggle } from "@/components/PushToggle";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Zap, title: "Lightning fast", text: "Opens instantly, full-screen — no browser bar, just like a native app." },
  { icon: Bell, title: "Order updates", text: "Track deliveries in real time from your home screen." },
  { icon: WifiOff, title: "Works offline", text: "Browse what you've already opened even with a weak signal." },
  { icon: MousePointerClick, title: "One tap away", text: "Your SHOP NOW icon sits right on your home screen." },
];

export default function GetApp() {
  const { canInstall, installed, promptInstall } = usePWAInstall();
  const url = typeof window !== "undefined" ? window.location.origin : "https://marketplace-jamaica.emergent.host";
  const [busy, setBusy] = useState(false);

  const handleInstall = async () => {
    setBusy(true);
    const res = await promptInstall();
    setBusy(false);
    if (res === "accepted") toast.success("Installing SHOP NOW…");
    else if (res === "unavailable") toast.info("Use your browser menu → 'Add to Home Screen' to install.");
  };

  const downloadPoster = () => {
    const svg = document.querySelector('[data-testid="app-qr-code"] svg');
    const svgHtml = svg ? svg.outerHTML : "";
    const logo = `${url}/shopnow-logo.png`;
    const w = window.open("", "_blank", "width=820,height=1160");
    if (!w) { toast.error("Please allow pop-ups to download the poster."); return; }
    const html = `<!doctype html><html><head><meta charset="utf-8" />
      <title>SHOP NOW — Scan to Install</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;font-family:'Outfit',Arial,Helvetica,sans-serif}
        body{background:#0B131F;color:#fff;display:flex;justify-content:center;padding:32px}
        .poster{width:720px;background:#0B131F;border-radius:28px;padding:56px 48px;text-align:center;border:2px solid #1c2734}
        .brand{display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:8px}
        .brand img{height:64px;width:64px;object-fit:contain}
        .brand b{font-size:34px;font-weight:800;letter-spacing:.5px}
        .brand b span{color:#E11B22}
        .slogan{color:#9fb0bf;font-size:15px;margin-bottom:36px}
        h1{font-size:52px;font-weight:800;line-height:1.05;margin-bottom:12px}
        h1 .r{color:#E11B22}
        .sub{color:#c7d2da;font-size:19px;margin-bottom:36px}
        .qr{background:#fff;display:inline-block;padding:28px;border-radius:24px}
        .qr svg{display:block}
        .url{margin-top:26px;font-size:20px;font-weight:700;color:#fff}
        .steps{margin-top:30px;color:#9fb0bf;font-size:15px;line-height:1.7}
        .foot{margin-top:34px;color:#5f7183;font-size:13px}
        @media print{body{padding:0;background:#0B131F}.poster{border:none}}
      </style></head>
      <body><div class="poster">
        <div class="brand"><img src="${logo}" alt="logo" /><b>SHOP&nbsp;NOW <span>Jamaica</span></b></div>
        <div class="slogan">Fastest delivery service worldwide 🚚 ✈️ 🛳️ 🚀</div>
        <h1>Scan to <span class="r">install</span><br/>our app</h1>
        <div class="sub">Shop, book stays &amp; track deliveries — right from your phone.</div>
        <div class="qr">${svgHtml}</div>
        <div class="url">${url.replace(/^https?:\/\//, "")}</div>
        <div class="steps">📷 Open your phone camera &amp; point it at the code — then tap “Add to Home Screen”.</div>
        <div class="foot">SHOP NOW · The Global Marketplace — Jamaica &amp; beyond</div>
      </div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    w.location.href = URL.createObjectURL(blob);
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 700);
  };

  return (
    <div className="bg-background" data-testid="get-app-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="rounded-3xl sn-ink-bg sn-noise relative overflow-hidden p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center text-white">
          <div className="relative">
            <span className="inline-flex items-center gap-2 sn-glass sn-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-wider"><Smartphone size={14} /> SHOP NOW App</span>
            <h1 className="font-clash font-bold text-4xl sm:text-5xl mt-4 leading-tight">Get SHOP NOW<br />on your phone.</h1>
            <p className="text-white/70 mt-4 max-w-md">Install our app straight from your browser — no app store, no waiting. Shop, book stays, track orders and chat with SHOP AI, all from your home screen.</p>

            {installed ? (
              <div className="mt-6 inline-flex items-center gap-2 bg-emerald/20 text-emerald rounded-xl px-5 py-3 font-semibold" data-testid="already-installed">
                <CheckCircle2 size={18} /> App installed — enjoy!
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 mt-6 items-center">
                <button onClick={handleInstall} disabled={busy} data-testid="install-app-btn"
                  className="inline-flex items-center gap-2 bg-coral text-white rounded-xl px-6 py-3.5 font-semibold hover:bg-white hover:text-ink transition-colors disabled:opacity-60">
                  <Download size={18} /> {canInstall ? "Install App" : "How to install"}
                </button>
                <span className="text-white/60 text-sm max-w-[220px]">{canInstall ? "One tap to add SHOP NOW to your device." : "Follow the quick steps below for your device."}</span>
              </div>
            )}
          </div>

          <div className="relative flex flex-col items-center">
            <div className="bg-white rounded-3xl p-6 shadow-2xl" data-testid="app-qr-code">
              <QRCodeSVG value={url} size={200} level="M" fgColor="#0B131F" bgColor="#ffffff" imageSettings={{ src: "/shopnow-logo.png", height: 40, width: 40, excavate: true }} />
            </div>
            <p className="text-white/70 text-sm mt-4 text-center max-w-[240px]">Scan with your phone camera to open &amp; install SHOP NOW</p>
            <button onClick={downloadPoster} data-testid="download-poster-btn"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold sn-glass rounded-xl px-5 py-2.5 hover:bg-white/20 transition-colors">
              <Printer size={16} /> Download "Scan to install" poster
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2">
        <div className="bg-card rounded-2xl border border-border p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="getapp-push">
          <span className="h-11 w-11 rounded-xl bg-coral/15 text-coral grid place-items-center shrink-0"><Bell size={22} /></span>
          <div className="flex-1">
            <h2 className="font-display font-bold text-lg">Never miss an order update</h2>
            <p className="text-sm text-muted-foreground">Turn on push notifications to get a ping the moment your order is packed, dispatched and delivered.</p>
          </div>
          <PushToggle variant="inline" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card rounded-2xl border border-border p-7" data-testid="ios-instructions">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><span className="text-coral"></span> On iPhone / iPad (Safari)</h2>
            <ol className="mt-4 space-y-3 text-sm text-slate-700">
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">1</span> Open <strong>shopnow.live</strong> in <strong>Safari</strong>.</li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">2</span> Tap the <Share size={15} className="inline -mt-0.5" /> <strong>Share</strong> button.</li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">3</span> Choose <strong>“Add to Home Screen”</strong> <Plus size={15} className="inline -mt-0.5" />.</li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">4</span> Tap <strong>Add</strong> — the SHOP NOW icon appears on your home screen.</li>
            </ol>
          </div>
          <div className="bg-card rounded-2xl border border-border p-7" data-testid="android-instructions">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><span className="text-emerald"></span> On Android (Chrome)</h2>
            <ol className="mt-4 space-y-3 text-sm text-slate-700">
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">1</span> Open <strong>shopnow.live</strong> in <strong>Chrome</strong>.</li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">2</span> Tap the <strong>Install App</strong> button above, or the <strong>⋮</strong> menu.</li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">3</span> Choose <strong>“Install app”</strong> / <strong>“Add to Home screen”</strong>.</li>
              <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-ink text-white grid place-items-center text-xs font-bold shrink-0">4</span> Confirm <strong>Install</strong> — done!</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-card rounded-2xl border border-border p-6 sn-card-hover">
              <b.icon size={22} className="text-coral mb-3" />
              <h3 className="font-display font-bold text-base">{b.title}</h3>
              <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-8">
          Native App Store &amp; Google Play versions are coming soon.{" "}
          <Link to="/support" className="text-coral font-semibold hover:underline">Questions? Contact support</Link>
        </p>
      </section>
    </div>
  );
}
